
import { useCallback, useEffect, useMemo, useState } from "react";

import CrudListing from "components/crud/CrudListing";
import SchoolModal from "./SchoolModal";
import { schoolColumns } from "./columns";

import {
  getSchools,
  deleteSchool,
  getSchoolAssignableEmployees,
  getAllSchoolsForFilter,
  importSchools,
  getSchoolFilterCountries,
  getSchoolFilterStates,
  getSchoolFilterDistricts,
  getSchoolFilterCities,
} from "services/api/schoolApi";

// ============================================================
// NORMALIZE API RESPONSE
// Supports Axios responses, nested data.items and plain arrays.
// ============================================================

const getRecords = (response, keys = []) => {
  if (Array.isArray(response)) {
    return response;
  }

  const body = response?.data ?? response;

  if (Array.isArray(body)) {
    return body;
  }

  const candidates = [
    body?.data?.items,
    body?.data?.records,
    body?.data?.schools,
    body?.data?.employees,
    ...keys.map((key) => body?.data?.[key]),
    ...keys.map((key) => body?.[key]),
    body?.items,
    body?.records,
    body?.schools,
    body?.employees,
    body?.data,
  ];

  return candidates.find(Array.isArray) || [];
};

// ============================================================
// GET ID
// ============================================================

const getId = (item) =>
  item?.id ??
  item?.employeeId ??
  item?.countryId ??
  item?.stateId ??
  item?.districtId ??
  item?.cityId ??
  item?.territoryId ??
  null;

// ============================================================
// GET LABEL
// ============================================================

const getLabel = (item, fallback = "Unknown") =>
  item?.name ||
  item?.label ||
  item?.schoolName ||
  item?.school_name ||
  item?.user?.name ||
  item?.employeeName ||
  item?.employeeCode ||
  item?.code ||
  fallback;

// ============================================================
// MAP RECORDS TO SELECT OPTIONS
// ============================================================

const toOptions = (records, labelGetter) => {
  const unique = new Map();

  if (!Array.isArray(records)) {
    return [];
  }

  records.forEach((item) => {
    const id = getId(item);

    if (id === null || id === undefined || String(id).trim() === "") {
      return;
    }

    const value = String(id);

    if (!unique.has(value)) {
      unique.set(value, {
        value,
        label: String(labelGetter(item)),
      });
    }
  });

  return Array.from(unique.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

// ============================================================
// DISTINCT OPTIONS FOR SCHOOL ATTRIBUTES
// ============================================================

const toDistinctOptions = (records, getter) => {
  const unique = new Map();

  if (!Array.isArray(records)) {
    return [];
  }

  records.forEach((item) => {
    const rawValue = getter(item);

    if (
      rawValue === null ||
      rawValue === undefined ||
      String(rawValue).trim() === ""
    ) {
      return;
    }

    const value = String(rawValue).trim();

    if (!unique.has(value)) {
      unique.set(value, {
        value,
        label: value,
      });
    }
  });

  return Array.from(unique.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

// ============================================================
// STATUS OPTIONS
// ============================================================

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "prospect", label: "Prospect" },
  { value: "closed", label: "Closed" },
];

// ============================================================
// SCHOOLS COMPONENT
// ============================================================

export default function Schools() {
  // ----------------------------------------------------------
  // STATES
  // ----------------------------------------------------------

  const [employees, setEmployees] = useState([]);
  const [schoolsForFilter, setSchoolsForFilter] = useState([]);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // ----------------------------------------------------------
  // LOAD EMPLOYEES AND SCHOOL FILTER OPTIONS
  // Existing flow retained.
  // ----------------------------------------------------------

  useEffect(() => {
    let mounted = true;

    const loadFilterOptions = async () => {
      setLoadingFilters(true);

      try {
        const results = await Promise.allSettled([
          getSchoolAssignableEmployees(),
          getAllSchoolsForFilter(),
        ]);

        if (!mounted) return;

        if (results[0].status === "fulfilled") {
          setEmployees(
            getRecords(results[0].value, ["employees"])
          );
        } else {
          console.error(
            "Unable to load employee filter options:",
            results[0].reason
          );
          setEmployees([]);
        }

        if (results[1].status === "fulfilled") {
          setSchoolsForFilter(
            getRecords(results[1].value, ["schools"])
          );
        } else {
          console.error(
            "Unable to load school filter options:",
            results[1].reason
          );
          setSchoolsForFilter([]);
        }
      } catch (error) {
        console.error("Unable to load school filters:", error);

        if (mounted) {
          setEmployees([]);
          setSchoolsForFilter([]);
        }
      } finally {
        if (mounted) {
          setLoadingFilters(false);
        }
      }
    };

    loadFilterOptions();

    return () => {
      mounted = false;
    };
  }, []);

  // ----------------------------------------------------------
  // LOAD COUNTRIES
  //
  // API response example:
  // { status: true, data: { items: [{ id: "1", name: "India" }] } }
  // ----------------------------------------------------------

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);

    try {
      const response = await getSchoolFilterCountries();
      const records = getRecords(response, ["countries"]);

      console.log("Countries API records:", records);

      setCountries(records);
    } catch (error) {
      console.error("Country filter loading failed:", error);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  // ----------------------------------------------------------
  // LOAD STATES BY COUNTRY
  // ----------------------------------------------------------

  const loadStates = useCallback(async (countryId) => {
    setStates([]);
    setDistricts([]);
    setCities([]);

    if (!countryId) return;

    setLoadingStates(true);

    try {
      const response = await getSchoolFilterStates(countryId);
      const records = getRecords(response, ["states"]);

      console.log("States API records:", records);

      setStates(records);
    } catch (error) {
      console.error("State filter loading failed:", error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  }, []);

  // ----------------------------------------------------------
  // LOAD DISTRICTS BY STATE
  // ----------------------------------------------------------

  const loadDistricts = useCallback(async (stateId) => {
    setDistricts([]);
    setCities([]);

    if (!stateId) return;

    setLoadingDistricts(true);

    try {
      const response = await getSchoolFilterDistricts(stateId);
      const records = getRecords(response, ["districts"]);

      console.log("Districts API records:", records);

      setDistricts(records);
    } catch (error) {
      console.error("District filter loading failed:", error);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  }, []);

  // ----------------------------------------------------------
  // LOAD CITIES BY DISTRICT
  // ----------------------------------------------------------

  const loadCities = useCallback(async (districtId) => {
    setCities([]);

    if (!districtId) return;

    setLoadingCities(true);

    try {
      const response = await getSchoolFilterCities(districtId);
      const records = getRecords(response, ["cities"]);

      console.log("Cities API records:", records);

      setCities(records);
    } catch (error) {
      console.error("City filter loading failed:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  // Load countries on initial render.
  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  // ----------------------------------------------------------
  // EMPLOYEE OPTIONS
  // ----------------------------------------------------------

  const employeeOptions = useMemo(
    () =>
      toOptions(employees, (employee) =>
        getLabel(employee, `Employee ${getId(employee)}`)
      ),
    [employees]
  );

  // ----------------------------------------------------------
  // BOARD OPTIONS
  // ----------------------------------------------------------

  const boardOptions = useMemo(
    () =>
      toDistinctOptions(
        schoolsForFilter,
        (school) => school?.board
      ),
    [schoolsForFilter]
  );

  // ----------------------------------------------------------
  // SCHOOL TYPE OPTIONS
  // ----------------------------------------------------------

  const typeOptions = useMemo(
    () =>
      toDistinctOptions(
        schoolsForFilter,
        (school) => school?.type
      ),
    [schoolsForFilter]
  );

  // ----------------------------------------------------------
  // MEDIUM OPTIONS
  // ----------------------------------------------------------

  const mediumOptions = useMemo(
    () =>
      toDistinctOptions(
        schoolsForFilter,
        (school) => school?.medium
      ),
    [schoolsForFilter]
  );

  // ----------------------------------------------------------
  // OWNERSHIP OPTIONS
  // ----------------------------------------------------------

  const ownershipOptions = useMemo(
    () =>
      toDistinctOptions(
        schoolsForFilter,
        (school) => school?.ownership
      ),
    [schoolsForFilter]
  );

  // ----------------------------------------------------------
  // SCHOOL LEVEL OPTIONS
  // ----------------------------------------------------------

  const schoolLevelOptions = useMemo(
    () =>
      toDistinctOptions(
        schoolsForFilter,
        (school) => school?.schoolLevel
      ),
    [schoolsForFilter]
  );

  // ----------------------------------------------------------
  // COUNTRY OPTIONS
  //
  // IMPORTANT: Use the countries API state, not school records.
  // ----------------------------------------------------------

  const countryOptions = useMemo(
    () =>
      toOptions(countries, (country) =>
        getLabel(country, `Country ${getId(country)}`)
      ),
    [countries]
  );

  // ----------------------------------------------------------
  // STATE OPTIONS
  // Only states returned for the selected country.
  // ----------------------------------------------------------

  const stateOptions = useMemo(
    () =>
      toOptions(states, (state) =>
        getLabel(state, `State ${getId(state)}`)
      ),
    [states]
  );

  // ----------------------------------------------------------
  // DISTRICT OPTIONS
  // Only districts returned for the selected state.
  // ----------------------------------------------------------

  const districtOptions = useMemo(
    () =>
      toOptions(districts, (district) =>
        getLabel(district, `District ${getId(district)}`)
      ),
    [districts]
  );

  // ----------------------------------------------------------
  // CITY OPTIONS
  // Only cities returned for the selected district.
  // ----------------------------------------------------------

  const cityOptions = useMemo(
    () =>
      toOptions(cities, (city) =>
        getLabel(city, `City ${getId(city)}`)
      ),
    [cities]
  );

  // ----------------------------------------------------------
  // TERRITORY OPTIONS
  // Existing school-based behavior retained.
  // ----------------------------------------------------------

  const territoryOptions = useMemo(
    () =>
      toOptions(
        schoolsForFilter
          .map((school) => school?.territory)
          .filter(Boolean),
        (territory) =>
          getLabel(territory, `Territory ${getId(territory)}`)
      ),
    [schoolsForFilter]
  );

  // ----------------------------------------------------------
  // FILTER CONFIGURATION
  // ----------------------------------------------------------

  const filters = useMemo(
    () => [
      {
        key: "assignedEmployeeId",
        label: "Assigned Employee",
        type: "select",
         searchable: true,
        placeholder: loadingFilters
          ? "Loading Employees..."
          : "All Employees",
        options: employeeOptions,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        placeholder: "All Statuses",
        options: STATUS_OPTIONS,
      },
      {
        key: "board",
        label: "Board",
        type: "select",
        placeholder: "All Boards",
        options: boardOptions,
      },
      {
        key: "type",
        label: "School Type",
        type: "select",
        placeholder: "All Types",
        options: typeOptions,
      },
      {
        key: "medium",
        label: "Medium",
        type: "select",
        placeholder: "All Mediums",
        options: mediumOptions,
      },
      {
        key: "ownership",
        label: "Ownership",
        type: "select",
        placeholder: "All Ownership Types",
        options: ownershipOptions,
      },
      {
        key: "schoolLevel",
        label: "School Level",
        type: "select",
        placeholder: "All School Levels",
        options: schoolLevelOptions,
      },
      {
        key: "countryId",
        label: "Country",
        type: "select",
        placeholder: loadingCountries
          ? "Loading Countries..."
          : "All Countries",
        options: countryOptions,
        onChange: (countryId) => {
          loadStates(countryId);
        },
        clearKeys: ["stateId", "districtId", "cityId"],
      },
      {
        key: "stateId",
        label: "State",
        type: "select",
        placeholder: loadingStates
          ? "Loading States..."
          : "All States",
        options: stateOptions,
        onChange: (stateId) => {
          loadDistricts(stateId);
        },
        clearKeys: ["districtId", "cityId"],
      },
      {
        key: "districtId",
        label: "District",
        type: "select",
        placeholder: loadingDistricts
          ? "Loading Districts..."
          : "All Districts",
        options: districtOptions,
        onChange: (districtId) => {
          loadCities(districtId);
        },
        clearKeys: ["cityId"],
      },
      {
        key: "cityId",
        label: "City",
        type: "select",
        placeholder: loadingCities
          ? "Loading Cities..."
          : "All Cities",
        options: cityOptions,
      },
      {
        key: "territoryId",
        label: "Territory",
        type: "select",
        placeholder: "All Territories",
        options: territoryOptions,
      },
    ],
    [
      loadingFilters,
      loadingCountries,
      loadingStates,
      loadingDistricts,
      loadingCities,
      employeeOptions,
      boardOptions,
      typeOptions,
      mediumOptions,
      ownershipOptions,
      schoolLevelOptions,
      countryOptions,
      stateOptions,
      districtOptions,
      cityOptions,
      territoryOptions,
      loadStates,
      loadDistricts,
      loadCities,
    ]
  );

  // ----------------------------------------------------------
  // FETCH SCHOOL LIST
  // Existing CRUD/listing response shape retained.
  // ----------------------------------------------------------

  const fetchData = useCallback(
    async (page, limit, search, activeFilters = {}) => {
      const cleanedFilters = Object.fromEntries(
        Object.entries(activeFilters || {}).filter(
          ([, value]) =>
            value !== null &&
            value !== undefined &&
            String(value).trim() !== ""
        )
      );

      const response = await getSchools(
        page,
        limit,
        search,
        cleanedFilters
      );

      const responseBody = response?.data?.data ?? response?.data ?? {};

      const records =
        responseBody?.schools ??
        responseBody?.items ??
        responseBody?.records ??
        [];

      return {
        data: {
          data: {
            records: Array.isArray(records) ? records : [],
            pagination: responseBody?.pagination || {},
          },
        },
      };
    },
    []
  );

  // ----------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------

  return (
    <CrudListing
      title="Schools"
      columns={schoolColumns}
      Modal={SchoolModal}
      fetchData={fetchData}
      deleteData={deleteSchool}
      responseDataKey="records"
      showSearch={true}
      filters={filters}
      addButtonText="Add School"
      importData={importSchools}
      importAccept=".csv,.xlsx,.xls"
    />
  );
}
